
import React from 'react';
import { cn } from '@/lib/utils';

interface PasswordStrengthIndicatorProps {
  password: string;
  className?: string;
}

export const PasswordStrengthIndicator: React.FC<PasswordStrengthIndicatorProps> = ({
  password,
  className
}) => {
  const getPasswordStrength = (password: string) => {
    if (!password) return { score: 0, label: '', color: '' };
    
    let score = 0;
    const checks = {
      length: password.length >= 8,
      lowercase: /[a-z]/.test(password),
      uppercase: /[A-Z]/.test(password),
      numbers: /\d/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password)
    };
    
    score = Object.values(checks).filter(Boolean).length;
    
    if (score < 2) return { score, label: 'Weak', color: 'bg-red-500' };
    if (score < 4) return { score, label: 'Fair', color: 'bg-yellow-500' };
    if (score < 5) return { score, label: 'Good', color: 'bg-blue-500' };
    return { score, label: 'Strong', color: 'bg-green-500' };
  };

  const strength = getPasswordStrength(password);
  
  if (!password) return null;

  return (
    <div className={cn("mt-2 space-y-2", className)}>
      <div className="flex items-center space-x-2">
        <div className="flex-1 bg-gray-200 rounded-full h-2">
          <div
            className={cn("h-2 rounded-full transition-all duration-300", strength.color)}
            style={{ width: `${(strength.score / 5) * 100}%` }}
          />
        </div>
        <span className={cn("text-xs font-medium", {
          'text-red-500': strength.score < 2,
          'text-yellow-500': strength.score >= 2 && strength.score < 4,
          'text-blue-500': strength.score >= 4 && strength.score < 5,
          'text-green-500': strength.score === 5
        })}>
          {strength.label}
        </span>
      </div>
      <div className="text-xs text-muted-foreground space-y-1">
        <div className="flex flex-wrap gap-2">
          <span className={password.length >= 8 ? 'text-green-600' : 'text-gray-400'}>
            ✓ 8+ characters
          </span>
          <span className={/[a-z]/.test(password) ? 'text-green-600' : 'text-gray-400'}>
            ✓ lowercase
          </span>
          <span className={/[A-Z]/.test(password) ? 'text-green-600' : 'text-gray-400'}>
            ✓ uppercase
          </span>
          <span className={/\d/.test(password) ? 'text-green-600' : 'text-gray-400'}>
            ✓ number
          </span>
          <span className={/[!@#$%^&*(),.?":{}|<>]/.test(password) ? 'text-green-600' : 'text-gray-400'}>
            ✓ special char
          </span>
        </div>
      </div>
    </div>
  );
};
